import _ from "lodash";
import Guid from "~/helpers/guid";
import { createNode, setPosition } from '~/modules/nodeManager';

/**
 * In-house tooltip implementation.
 * This is used via the 'data-c-tooltip' HTML attribute, and via the 'v-tooltip' Vue directive.
 * In Vue apps, please always use the Vue directive going forward.
 */
export default class CTooltip {
  $doc: any = document;
  tooltipText: string;
  uid: Guid;
  $nodeTarget: JQuery<Document | HTMLElement>;
  tooltipContentNode: HTMLElement;

  /* ------------------------------------------ */

  constructor(private options: { html: boolean } = { html: false }) {
    this.uid = Guid.create()
  }

  get tooltipNamespace() {
    return `ctooltip-${this.uid}`
  }

  init($parentElement: JQuery<Document | HTMLElement> = $(document), tooltipText: string = null): CTooltip {

    // this is where the 'switch' between [data-c-tooltip] vs the Vue directive happens. if needed, you can
    // instead require the method caller to specify.
    if (tooltipText) {
      // the Vue directive uses this flow
      this.tooltipText = tooltipText;
      this.$nodeTarget = $parentElement;
    } else {
      // the .init() calls use this flow
      this.$nodeTarget = $parentElement.find('[data-c-tooltip]').addBack('[data-c-tooltip]');
    }

    const cancelDebounce = () => {
      if (window.AppConfig.currentTooltipDebounce) {
        window.AppConfig.currentTooltipDebounce.cancel();
        window.AppConfig.currentTooltipDebounce = null;
      }
    }

    // Setup Listeners
    this.$nodeTarget.off(this.tooltipNamespace);

    this.$nodeTarget.on(`mouseenter.${this.tooltipNamespace}`, (e) => {
      cancelDebounce();

      window.AppConfig.currentTooltipDebounce = _.debounce(() => {
        window.requestAnimationFrame(() => this.createTooltipContainer(e))
      }, window.AppConfig.tooltipDebounceTimeout());

      window.AppConfig.currentTooltipDebounce(e);
    });

    this.$nodeTarget.on(`mouseout.${this.tooltipNamespace}`, (e) => {
      window.requestAnimationFrame(() => this.deleteTooltipContainer())
    })

    return this;
  }

  static registerClearTooltipHook() {
    /**
     *   # Usage
     *
     *   When you add a custom event that will
     *   not trigger default tooltip clearing
     *   Do so manually from your current scope
     *
     *   `$(document).trigger('cTooltip:clear')`
     */
    $(document).on('cTooltip:clear', (e) => {
      const tooltipContainer = $(e.target).find('#tooltip-container');
      if (tooltipContainer.length) {
        tooltipContainer.remove();
      }
    });
  }

  static clearTooltips() {
    const $doc = $(document)
    $doc.promise().then(($e) => $e.trigger('cTooltip:clear'))
  }

  updateTooltipText(tooltipText: string): void {
    this.tooltipText = tooltipText;
    if (this.tooltipContentNode) {
      this.tooltipContentNode.textContent = this.tooltipText;
    }
  }

  getTooltipText(event, selector) {
    const target = event.currentTarget;
    return target.getAttribute(selector);
  }

  createTooltipContainer(event) {
    try {
      if (!$(event.currentTarget).filter(":hover, :focus").length) return
      const hasTooltip = $('aside#tooltip-container').length;
      if (hasTooltip) return

      let tooltipText = this.tooltipText || this.getTooltipText(event, 'data-c-tooltip');
      if (!tooltipText) return

      const customClass = event.currentTarget.getAttribute('data-c-tooltip-class');
      const isJSON = event.currentTarget.getAttribute('data-c-tooltip-json') === "true";

      const asideParams = {
        type: 'aside',
        attributes: [
          { name: 'id', value: 'tooltip-container' }
        ]
      };
      if (customClass) {
        asideParams['attributes'].push({ name: 'class', value: customClass });
      }

      const asideNode = createNode(asideParams);
      if (isJSON) {
        tooltipText = JSON.parse(`{"options" : ${tooltipText}}`);
        tooltipText.options.map(
          (item) => {
            let contentParams = {
              text: item.text,
              type: item.tag,
              parent: asideNode
            };
            this.tooltipContentNode = createNode(contentParams);
          }
        );
      } else {
        const contentParams = {
          text: tooltipText,
          type: 'p',
          parent: asideNode,
          html: Boolean(this.options.html),
        };
        this.tooltipContentNode = createNode(contentParams);
      }

      let leftAdjustment = 0;
      if (customClass) {
        if (customClass.includes('right')) {
          leftAdjustment = event.currentTarget.offsetWidth;
        } else if (customClass.includes('left')) {
          leftAdjustment = -event.currentTarget.offsetWidth;
        }
      }

      setPosition(event, asideNode, leftAdjustment, 0, 'relative');
    } catch {

    }
  }

  /**
   * This only hides the visible tooltip. It doesn't prevent the tooltip from reappearing again.
   */
  deleteTooltipContainer() {
    const tooltipContainer = this.$doc.getElementById('tooltip-container');
    if (tooltipContainer) tooltipContainer.remove();
  }
}

window.addEventListener('DOMContentLoaded', () => {
  CTooltip.registerClearTooltipHook();
});
